import React, { useState, useEffect } from 'react';
import { X, Mail, Plus, Trash2, Save } from 'lucide-react';
import { reportsAPI } from '../services/part2-api';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const EmailReportModal = ({ isOpen, onClose, reportType, dateRange, businessId, reportName }) => {
  const [emails, setEmails] = useState(['']);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [format, setFormat] = useState('pdf');
  const [caEmail, setCaEmail] = useState('');
  const [saveCaEmail, setSaveCaEmail] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Fetch saved CA email
      fetchCaEmail();
      // Set default subject
      setSubject(`${reportName} - ${dateRange?.from_date || 'All'} to ${dateRange?.to_date || 'Today'}`);
    }
  }, [isOpen, reportName, dateRange]);

  const fetchCaEmail = async () => {
    try {
      const response = await api.get('/reports/ca-email');
      if (response.data.ca_email) {
        setCaEmail(response.data.ca_email);
      }
    } catch (error) {
      console.error('Failed to fetch CA email:', error);
    }
  };

  const addEmailField = () => {
    setEmails([...emails, '']);
  };

  const removeEmailField = (index) => {
    const newEmails = emails.filter((_, i) => i !== index);
    setEmails(newEmails.length > 0 ? newEmails : ['']);
  };

  const updateEmail = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const useCaEmail = () => {
    if (caEmail) {
      setEmails([caEmail, ...emails.filter(e => e !== '')]);
    }
  };

  const handleSend = async () => {
    try {
      setSending(true);
      
      const validEmails = emails.filter(e => e.trim() !== '');
      
      if (validEmails.length === 0) {
        toast.error('Please enter at least one email address');
        return;
      }

      const response = await reportsAPI.emailReport({
        report_type: reportType,
        format: format,
        to_emails: validEmails,
        subject: subject,
        message: message,
        from_date: dateRange?.from_date,
        to_date: dateRange?.to_date,
        save_ca_email: saveCaEmail
      });

      if (response.data.success) {
        const emailResult = response.data.email_result;
        
        if (emailResult.mode === 'dev_mock') {
          toast.info('Email logged in dev mode. Configure SMTP to send real emails.');
        } else if (emailResult.sent) {
          toast.success('Email sent successfully!');
        } else {
          toast.error('Email sending failed: ' + emailResult.message);
        }
        
        onClose();
      }
      
    } catch (error) {
      console.error('Email error:', error);
      toast.error('Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="w-6 h-6 text-white" />
            <h2 className="text-xl font-bold text-white">Email Report</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Format Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attachment Format
            </label>
            <div className="flex gap-3">
              {['pdf', 'excel', 'json'].map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => setFormat(fmt)}
                  className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                    format === fmt
                      ? 'border-teal-500 bg-teal-50 text-teal-700 font-medium'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
                >
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* CA Email Shortcut */}
          {caEmail && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-900">CA Email Saved</p>
                  <p className="text-sm text-blue-700">{caEmail}</p>
                </div>
                <button
                  onClick={useCaEmail}
                  className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Use CA Email
                </button>
              </div>
            </div>
          )}

          {/* Email Recipients */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipients
            </label>
            {emails.map((email, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => updateEmail(index, e.target.value)}
                  placeholder="email@example.com"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                {emails.length > 1 && (
                  <button
                    onClick={() => removeEmailField(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={addEmailField}
              className="flex items-center gap-2 px-3 py-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Another Recipient
            </button>
          </div>

          {/* Save CA Email Option */}
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={saveCaEmail}
                onChange={(e) => setSaveCaEmail(e.target.checked)}
                className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">
                Save first email as CA email for quick access
              </span>
            </label>
          </div>

          {/* Subject */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          {/* Message */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message (Optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Add a custom message to the email..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="px-6 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {sending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                Send Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailReportModal;
